/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  Grid,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { BootstrapDialog } from '../../../../assets/style/globalStyle';
import { AppWidgetSummary } from '../../../sections/dashboard/dash';
import ReactApexChart from 'react-apexcharts';
import { BaseOptionChart } from '../../../components/chart';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import configAxios from '../../../../services/configAxios';
import { NotificationManager } from 'react-notifications';

const contractIndicators = (props) => {
  const [indicators, setIndicators] = useState({
    total: 0,
    titulares: 0,
    dependentes: 0,
    idadeMedia: 0,
    distribuicaoFaixaEtaria: []
  });

  useEffect(() => {
    if (props.clientId && props.openModal && isEmpresarial()) {
      fetchIndicators();
    }
  }, [props.clientId, props.openModal]);

  const isEmpresarial = () => {
    return props.otherInfos?.tpcontrat_client === '2-COLETIVO EMPRESARIAL';
  };

  const fetchIndicators = async () => {
    // Verifica se é contrato empresarial antes de buscar
    if (!isEmpresarial()) {
      return;
    }

    try {
      const response = await configAxios.get(`/indicators/contract?id_client=${props.clientId}`);
      setIndicators(response.data);
    } catch (error) {
      // console.log(error);
      NotificationManager.error(
        'Tivemos dificuldades para buscar os indicadores, tente novamente!',
        'Atenção'
      );
    }
  };

  const closeModal = () => {
    props.setOpenModal(false);
    setIndicators({
      total: 0,
      titulares: 0,
      dependentes: 0,
      idadeMedia: 0,
      distribuicaoFaixaEtaria: []
    });
  };

  const BootstrapDialogTitle = (props) => {
    const { children, onClose, ...other } = props;
    return (
      <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
        {children}
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 10,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
    );
  };

  BootstrapDialogTitle.propTypes = {
    children: PropTypes.node,
    onClose: PropTypes.func.isRequired
  };

  contractIndicators.propTypes = {
    openModal: PropTypes.bool.isRequired,
    setOpenModal: PropTypes.func.isRequired,
    clientId: PropTypes.number,
    otherInfos: PropTypes.object
  };

  return (
    <div>
      <BootstrapDialog
        onClose={() => props.setOpenModal(false)}
        aria-labelledby="indicators-dialog-title"
        open={props.openModal}
        maxWidth="lg"
        fullWidth>
        <BootstrapDialogTitle
          id="indicators-dialog-title"
          onClose={() => props.setOpenModal(false)}>
          Indicadores do contrato
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <Box sx={{ p: 2 }}>
            {!isEmpresarial() ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  Indicadores disponíveis apenas para contratos empresariais
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  O tipo de contrato atual é: {props.otherInfos?.tpcontrat_client || 'Não definido'}
                </Typography>
              </Box>
            ) : indicators ? (
              <>
                {/* Cards de Indicadores usando AppWidgetSummary */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetSummary
                      title="Total de beneficiários"
                      total={indicators.total}
                      icon="eva:people-fill"
                      color="primary"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetSummary
                      title="Titulares"
                      total={indicators.titulares}
                      icon="eva:person-fill"
                      color="info"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetSummary
                      title="Dependentes"
                      total={indicators.dependentes}
                      icon="eva:people-outline"
                      color="warning"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetSummary
                      title="Idade média"
                      total={`${indicators.idadeMedia} anos`}
                      icon="eva:calendar-outline"
                      color="success"
                    />
                  </Grid>
                </Grid>

                {/* Gráfico e Indicadores */}
                <Grid container spacing={3}>
                  {/* Gráfico de Distribuição por Faixa Etária */}
                  <Grid item xs={12} md={8}>
                    <Card>
                      <CardHeader title="Distribuição por faixa etária" />
                      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
                        {indicators.distribuicaoFaixaEtaria?.categorias &&
                        indicators.distribuicaoFaixaEtaria?.valores ? (
                          <ReactApexChart
                            type="bar"
                            series={[
                              {
                                name: 'Beneficiários',
                                data: indicators.distribuicaoFaixaEtaria.valores
                              }
                            ]}
                            options={{
                              ...BaseOptionChart(),
                              chart: {
                                type: 'bar',
                                toolbar: { show: false }
                              },
                              plotOptions: {
                                bar: {
                                  horizontal: false,
                                  columnWidth: '55%',
                                  endingShape: 'rounded'
                                }
                              },
                              dataLabels: {
                                enabled: false
                              },
                              stroke: {
                                show: true,
                                width: 2,
                                colors: ['transparent']
                              },
                              xaxis: {
                                categories: indicators.distribuicaoFaixaEtaria.categorias,
                                labels: {
                                  rotate: -45,
                                  style: {
                                    fontSize: '10px'
                                  }
                                }
                              },
                              yaxis: {
                                title: {
                                  text: 'Número de Beneficiários'
                                },
                                min: 0,
                                tickAmount: 5
                              },
                              fill: {
                                opacity: 1
                              },
                              tooltip: {
                                y: {
                                  formatter: (val) => `${val} beneficiários`
                                }
                              },
                              colors: ['#1976d2'],
                              grid: {
                                strokeDashArray: 3,
                                borderColor: '#e0e0e0'
                              }
                            }}
                            height={335}
                          />
                        ) : (
                          <Box sx={{ textAlign: 'center', p: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Carregando dados do gráfico...
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Indicadores */}
                  <Grid item xs={12} md={4}>
                    <Grid container spacing={2}>
                      {/* Razão de Dependência */}
                      <Grid item xs={12}>
                        <Card sx={{ p: 2 }}>
                          <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem' }}>
                            Razão de dependência
                          </Typography>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 'bold',
                                color:
                                  indicators.razaoDependenciaMaxima &&
                                  indicators.razaoDependencia <= indicators.razaoDependenciaMaxima
                                    ? 'success.main'
                                    : 'error.main',
                                mb: 1
                              }}>
                              {indicators.razaoDependencia}%
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  indicators.razaoDependenciaMaxima &&
                                  indicators.razaoDependencia <= indicators.razaoDependenciaMaxima
                                    ? 'success.main'
                                    : 'error.main',
                                fontWeight: 'bold'
                              }}>
                              {indicators.razaoDependenciaMaxima &&
                              indicators.razaoDependencia <= indicators.razaoDependenciaMaxima
                                ? 'Adequado'
                                : 'Inadequado'}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>

                      {/* Porcentagem de Idosos */}
                      <Grid item xs={12}>
                        <Card sx={{ p: 2 }}>
                          <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem' }}>
                            Porcentagem de idosos
                          </Typography>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 'bold',
                                color: 'primary.main',
                                mb: 1
                              }}>
                              {indicators.porcentagemIdosos || 0}%
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Nenhum dado disponível
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeModal()} variant="contained" color="error">
            Fechar
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </div>
  );
};

export default contractIndicators;
